import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import './HROverview.css';

const DEPT_COLORS = {
  'Sales':            '#3b82f6',
  'Accounts':         '#10b981',
  'Tech / Marketing': '#8b5cf6',
  'Warehouse':        '#f59e0b',
  'Unassigned':       '#94a3b8',
  'Driver':           '#06b6d4',
  'Incharge':         '#f97316',
};

const CITY_CONFIG = {
  Lahore:    { color: '#3b82f6', bg: '#eff6ff' },
  Karachi:   { color: '#10b981', bg: '#f0fdf4' },
  Islamabad: { color: '#8b5cf6', bg: '#f5f3ff' },
  Multan:    { color: '#f97316', bg: '#fff7ed' },
};

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarClass(name) {
  const c = ['ov-av-blue','ov-av-teal','ov-av-amber','ov-av-purple','ov-av-coral','ov-av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

function formatPKR(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `PKR ${(n/1000000).toFixed(1)}M`;
  if (n >= 1000) return `PKR ${(n/1000).toFixed(0)}K`;
  return `PKR ${Number(n).toLocaleString('en-PK')}`;
}

function formatTime() {
  return new Date().toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

function formatShortDate(d) {
  return new Date(d).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric' });
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ov-tooltip">
      <div className="ov-tooltip-label">{formatShortDate(label)}</div>
      {payload.map((p, i) => (
        <div key={i} className="ov-tooltip-row" style={{ color: p.color }}>
          <span>{p.name}:</span><span>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── AI REPORT COMPONENT ───────────────────────────────────────────────────────

function AIReport({ data, onClose }) {
  const [report, setReport]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const reportRef               = useRef('');

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    try {
      setLoading(true);
      setReport('');
      reportRef.current = '';

      const now   = new Date();
      const month = now.toLocaleString('default', { month: 'long' });
      const year  = now.getFullYear();

      // Build the prompt with all real data
      const prompt = `You are an HR analytics AI for Fotile Pakistan. Analyze the following attendance and salary data for ${month} ${year} and generate a comprehensive executive summary for the CEO/Boss.

DATA:
- Total Employees: ${data.totalEmployees}
- Currently Clocked In Today: ${data.clockedIn}
- Absent Today: ${data.absentToday}
- Late Today: ${data.lateToday}
- Attendance Rate Today: ${data.attendanceRate}%
- Total Net Payroll This Month: PKR ${Number(data.totalNetSalary).toLocaleString()}
- Total Salary Deductions This Month: PKR ${Number(data.totalDeductions).toLocaleString()}

DEPARTMENT BREAKDOWN:
${data.departments.map(d => `- ${d.department}: ${d.total} employees, ${d.present_today} present today, ${d.late_today} late today`).join('\n')}

EMPLOYEES WITH 3+ LATES THIS MONTH (at risk of deduction):
${data.lateAlerts.length > 0 ? data.lateAlerts.map(e => `- ${e.full_name} (${e.employee_id}): ${e.late_count} lates, ${e.city}, ${e.department || 'Unassigned'}`).join('\n') : 'None'}

EMPLOYEES ABSENT TODAY:
${data.absentList.slice(0, 10).map(e => `- ${e.full_name} (${e.employee_id}): ${e.department || 'Unassigned'}, ${e.city}`).join('\n')}

SALARY REPORTS (top deductions):
${data.salaryReports.slice(0, 5).map(r => `- ${r.employee_name} (${r.emp_code}): Base PKR ${Number(r.base_salary).toLocaleString()}, Deduction PKR ${Number(r.total_deduction).toLocaleString()} (${r.deduction_percent}%), Net PKR ${Number(r.net_salary).toLocaleString()}`).join('\n')}

14-DAY ATTENDANCE TREND:
${data.trend.slice(-7).map(t => `- ${t.date}: ${t.present_count} present, ${t.late_count} late, ${t.attendance_rate}% rate`).join('\n')}

Generate a professional executive summary with these sections:
1. **Overall Health Score** — rate the company attendance health as Excellent/Good/Needs Attention/Critical with a brief reason
2. **Today's Snapshot** — what's happening today
3. **Department Performance** — which department is doing best and worst, with specific observations
4. **Employees Requiring Attention** — name specific employees who are consistently late or absent, be direct
5. **Financial Impact** — total deductions, who is costing the most in deductions
6. **Recommendations** — 2-3 specific actionable recommendations for management

Be direct, specific, use employee names, use PKR amounts. Write in a professional but readable tone. Use emojis for section headers. Keep it comprehensive but scannable.`;

      const response = await fetch('https://fotileams.com/api/ai/summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      const text   = result.report || 'Failed to generate report.';
      setReport(text);
    } catch (err) {
      setError('Failed to generate AI report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format the report text with markdown-style formatting
  const formatReport = (text) => {
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();

      // H1 - # Title
      if (/^# /.test(trimmed)) {
        return <h2 key={i} className="ai-h1">{trimmed.replace(/^# /, '')}</h2>;
      }
      // H2 - ## Title
      if (/^## /.test(trimmed)) {
        return <h3 key={i} className="ai-section-title">{trimmed.replace(/^## /, '')}</h3>;
      }
      // H3 - ### Title
      if (/^### /.test(trimmed)) {
        return <h4 key={i} className="ai-h3">{trimmed.replace(/^### /, '')}</h4>;
      }
      // Horizontal rule
      if (/^---+$/.test(trimmed)) {
        return <hr key={i} className="ai-divider" />;
      }
      // Bullet points
      if (/^[-•*] /.test(trimmed)) {
        const inner = trimmed.replace(/^[-•*] /, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <li key={i} className="ai-li" dangerouslySetInnerHTML={{ __html: inner }} />;
      }
      // Numbered list
      if (/^\d+\./.test(trimmed)) {
        const inner = trimmed.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <li key={i} className="ai-li ai-li-num" dangerouslySetInnerHTML={{ __html: inner }} />;
      }
      // Empty line
      if (trimmed === '') return <div key={i} className="ai-spacer" />;
      // Regular paragraph with bold support
      const html = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="ai-para" dangerouslySetInnerHTML={{ __html: html }} />;
    });
  };

  return (
    <div className="ai-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-modal-header">
          <div className="ai-modal-title-row">
            <div className="ai-icon">✦</div>
            <div>
              <div className="ai-modal-title">AI Executive Summary</div>
              <div className="ai-modal-sub">
                {new Date().toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>
          <div className="ai-modal-actions">
            <button className="ai-regen-btn" onClick={generateReport} disabled={loading}>
              {loading ? '…' : '↻ Regenerate'}
            </button>
            <button className="ai-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="ai-modal-body">
          {loading ? (
            <div className="ai-loading">
              <div className="ai-loading-dots">
                <span /><span /><span />
              </div>
              <p>Claude is analyzing your attendance data…</p>
            </div>
          ) : error ? (
            <div className="ai-error">{error}</div>
          ) : (
            <div className="ai-report-content">
              {formatReport(report)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MAIN OVERVIEW ─────────────────────────────────────────────────────────────

export default function HROverview() {
  const [time, setTime]             = useState(formatTime());
  const [todayData, setTodayData]   = useState(null);
  const [trend, setTrend]           = useState([]);
  const [departments, setDepartments] = useState([]);
  const [alerts, setAlerts]         = useState(null);
  const [employees, setEmployees]   = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showAI, setShowAI]         = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTime(formatTime()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const now   = new Date();
      const month = now.getMonth() + 1;
      const year  = now.getFullYear();
      const [todayRes, trendRes, deptRes, alertsRes, empRes, salRes] = await Promise.all([
        api.getAllAttendance(),
        api.getAttendanceTrend(14),
        api.getDepartmentStats(),
        api.getAlerts(),
        api.getAllEmployees(),
        api.getAllSalaryReports({ month, year }),
      ]);
      setTodayData(todayRes);
      setTrend(trendRes.trend || []);
      setDepartments(deptRes.departments || []);
      setAlerts(alertsRes);
      setEmployees(empRes.employees || []);
      setSalaryData(salRes);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 120000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const totalEmployees = employees.length;
  const clockedIn      = todayData?.summary?.clocked_in  || 0;
  const clockedOut     = todayData?.summary?.clocked_out || 0;
  const lateToday      = todayData?.summary?.late        || 0;
  const totalPresent   = todayData?.summary?.total       || 0;
  const absentToday    = Math.max(0, totalEmployees - totalPresent);
  const attendanceRate = totalEmployees > 0 ? Math.round((totalPresent / totalEmployees) * 100) : 0;
  const totalDeductions = parseFloat(salaryData?.summary?.total_deductions || 0);
  const totalNetSalary  = parseFloat(salaryData?.summary?.total_net_salary  || 0);
  const lateAlertCount  = alerts?.late_alerts?.length || 0;

  const cityStats = ['Lahore','Karachi','Islamabad','Multan'].map(city => {
    const cityEmps    = employees.filter(e => e.city === city);
    const cityPresent = (todayData?.records || []).filter(r => r.city === city).length;
    return { city, total: cityEmps.length, present: cityPresent };
  });

  const trendData = trend.slice(-14).map(t => ({
    date: t.date, Present: t.present_count, Late: t.late_count,
  }));

  const now = new Date();

  // Build AI data payload
  const aiData = {
    totalEmployees, clockedIn, absentToday, lateToday, attendanceRate,
    totalNetSalary, totalDeductions,
    departments,
    lateAlerts:    alerts?.late_alerts || [],
    absentList:    alerts?.absent_today || [],
    salaryReports: salaryData?.reports || [],
    trend,
  };

  if (loading) {
    return (
      <div className="ov-loading">
        <div className="ov-spinner" />
        <p>Loading mission control…</p>
      </div>
    );
  }

  return (
    <div className="ov-root">

      {/* AI Report Modal */}
      {showAI && <AIReport data={aiData} onClose={() => setShowAI(false)} />}

      {/* TOP BAR */}
      <div className="ov-topbar">
        <div className="ov-topbar-left">
          <div className="ov-title-row">
            <h1 className="ov-title">Mission Control</h1>
            <span className="ov-live-badge">● LIVE</span>
          </div>
          <span className="ov-date">
            {now.toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="ov-topbar-right">
          <div className="ov-clock-box">
            <span className="ov-clock">{time}</span>
            <span className="ov-tz">PKT</span>
          </div>
          {lastRefresh && (
            <span className="ov-refresh-time">
              Refreshed {lastRefresh.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
          )}
          {/* AI BUTTON */}
          <button className="ov-ai-btn" onClick={() => setShowAI(true)}>
            ✦ AI Summary
          </button>
          <button className="ov-refresh-btn" onClick={fetchAll}>↻ Refresh</button>
        </div>
      </div>

      {/* ALERTS BANNER */}
      {lateAlertCount > 0 && (
        <div className="ov-alert-banner">
          <span className="ov-alert-icon">⚠</span>
          <span>{lateAlertCount} employee{lateAlertCount > 1 ? 's' : ''} have reached 3+ late arrivals this month — salary deduction will apply</span>
          <span className="ov-alert-action" onClick={() => setShowAI(true)}>View AI Analysis →</span>
        </div>
      )}

      {/* MAIN GRID */}
      <div className="ov-main-grid">
        <div className="ov-left-col">

          {/* KPI STRIP */}
          <div className="ov-kpi-strip">
            <div className="ov-kpi-card ov-kpi-blue">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Total Employees</span>
                <span className="ov-kpi-icon-sm">👥</span>
              </div>
              <div className="ov-kpi-value">{totalEmployees}</div>
              <div className="ov-kpi-sub">Active workforce</div>
            </div>
            <div className="ov-kpi-card ov-kpi-green">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Currently In</span>
                <span className="ov-kpi-pulse-dot" />
              </div>
              <div className="ov-kpi-value">{clockedIn}</div>
              <div className="ov-kpi-sub">{clockedOut} clocked out</div>
            </div>
            <div className="ov-kpi-card ov-kpi-red">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Absent Today</span>
                <span className="ov-kpi-icon-sm">✗</span>
              </div>
              <div className="ov-kpi-value">{absentToday}</div>
              <div className="ov-kpi-sub">Not clocked in</div>
            </div>
            <div className="ov-kpi-card ov-kpi-amber">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Late Today</span>
                <span className="ov-kpi-icon-sm">⏰</span>
              </div>
              <div className="ov-kpi-value">{lateToday}</div>
              <div className="ov-kpi-sub">Past grace period</div>
            </div>
            <div className="ov-kpi-card ov-kpi-purple">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Net Payroll</span>
                <span className="ov-kpi-icon-sm">₨</span>
              </div>
              <div className="ov-kpi-value ov-kpi-value-sm">{formatPKR(totalNetSalary)}</div>
              <div className="ov-kpi-sub ov-kpi-sub-red">{formatPKR(totalDeductions)} deducted</div>
            </div>
          </div>

          {/* TREND CHART */}
          <div className="ov-card">
            <div className="ov-card-header">
              <div>
                <div className="ov-card-title">14-Day Attendance Trend</div>
                <div className="ov-card-sub">Daily present count and late arrivals</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="presentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="lateGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={formatShortDate}
                  tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="Present" stroke="#3b82f6" strokeWidth={2}
                  fill="url(#presentGrad)" dot={false} />
                <Area type="monotone" dataKey="Late" stroke="#f59e0b" strokeWidth={2}
                  fill="url(#lateGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* CITY BREAKDOWN */}
          <div className="ov-city-grid">
            {cityStats.map(({ city, total, present }) => {
              const cfg  = CITY_CONFIG[city] || { color: '#94a3b8', bg: '#f8fafc' };
              const rate = total > 0 ? Math.round((present / total) * 100) : 0;
              return (
                <div key={city} className="ov-city-card" style={{ borderTopColor: cfg.color }}>
                  <div className="ov-city-name" style={{ color: cfg.color }}>{city}</div>
                  <div className="ov-city-present">{present}<span>/{total}</span></div>
                  <div className="ov-city-bar">
                    <div className="ov-city-fill" style={{ width: `${rate}%`, background: cfg.color }} />
                  </div>
                  <div className="ov-city-rate">{rate}% present</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="ov-right-col">

          {/* GAUGE */}
          <div className="ov-card ov-gauge-card">
            <div className="ov-card-title" style={{ marginBottom: 8 }}>Today's Rate</div>
            <div className="ov-gauge-wrap">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="54" fill="none" stroke="#f1f5f9" strokeWidth="12" />
                <circle cx="70" cy="70" r="54" fill="none"
                  stroke={attendanceRate >= 80 ? '#10b981' : attendanceRate >= 60 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="12"
                  strokeDasharray={`${2 * Math.PI * 54}`}
                  strokeDashoffset={`${2 * Math.PI * 54 * (1 - attendanceRate / 100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 70 70)"
                  style={{ transition: 'stroke-dashoffset 1s ease' }}
                />
                <text x="70" y="64" textAnchor="middle" fontSize="26" fontWeight="700" fill="#0f172a">
                  {attendanceRate}%
                </text>
                <text x="70" y="82" textAnchor="middle" fontSize="11" fill="#94a3b8">
                  Attendance
                </text>
              </svg>
            </div>
            <div className="ov-gauge-stats">
              <div className="ov-gauge-stat">
                <span className="ov-gauge-num ov-green">{totalPresent}</span>
                <span className="ov-gauge-lbl">Present</span>
              </div>
              <div className="ov-gauge-divider" />
              <div className="ov-gauge-stat">
                <span className="ov-gauge-num ov-red">{absentToday}</span>
                <span className="ov-gauge-lbl">Absent</span>
              </div>
            </div>
          </div>

          {/* DEPARTMENT */}
          <div className="ov-card">
            <div className="ov-card-title" style={{ marginBottom: 12 }}>By Department</div>
            {departments.filter(d => parseInt(d.total) > 0).map((d, i) => {
              const color   = DEPT_COLORS[d.department] || '#94a3b8';
              const total   = parseInt(d.total);
              const present = parseInt(d.present_today);
              const rate    = total > 0 ? Math.round((present / total) * 100) : 0;
              return (
                <div key={i} className="ov-dept-row">
                  <div className="ov-dept-info">
                    <span className="ov-dept-dot" style={{ background: color }} />
                    <span className="ov-dept-name">{d.department || 'Unassigned'}</span>
                  </div>
                  <div className="ov-dept-bar-wrap">
                    <div className="ov-dept-bar">
                      <div className="ov-dept-fill" style={{ width: `${rate}%`, background: color }} />
                    </div>
                    <span className="ov-dept-stat">{present}/{total}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ALERTS */}
          <div className="ov-card ov-alerts-card">
            <div className="ov-card-title" style={{ marginBottom: 10 }}>
              Alerts
              {lateAlertCount > 0 && (
                <span className="ov-alert-count">{lateAlertCount}</span>
              )}
            </div>
            {lateAlertCount === 0 && !alerts?.consecutive_absent?.length ? (
              <div className="ov-no-alerts">✓ All clear — no alerts today</div>
            ) : (
              <div className="ov-alerts-list">
                {alerts?.late_alerts?.slice(0, 4).map((emp, i) => (
                  <div key={i} className="ov-alert-row ov-alert-amber">
                    <div className={`ov-alert-av ${getAvatarClass(emp.full_name)}`}>
                      {getInitials(emp.full_name)}
                    </div>
                    <div className="ov-alert-info">
                      <span className="ov-alert-name">{emp.full_name}</span>
                      <span className="ov-alert-detail">{emp.late_count} lates · {emp.city}</span>
                    </div>
                    <span className="ov-alert-tag ov-tag-amber">
                      {emp.late_count >= 6 ? 'Full day' : 'Half day'}
                    </span>
                  </div>
                ))}
                {alerts?.consecutive_absent?.slice(0, 3).map((emp, i) => (
                  <div key={i} className="ov-alert-row ov-alert-red">
                    <div className={`ov-alert-av ${getAvatarClass(emp.full_name)}`}>
                      {getInitials(emp.full_name)}
                    </div>
                    <div className="ov-alert-info">
                      <span className="ov-alert-name">{emp.full_name}</span>
                      <span className="ov-alert-detail">{emp.absent_days} absences · {emp.city}</span>
                    </div>
                    <span className="ov-alert-tag ov-tag-red">Absent</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ABSENT TODAY */}
      {alerts?.absent_today?.length > 0 && (
        <div className="ov-card ov-absent-section">
          <div className="ov-card-header">
            <div>
              <div className="ov-card-title">Not Clocked In Today</div>
              <div className="ov-card-sub">{alerts.absent_today.length} employees haven't clocked in</div>
            </div>
          </div>
          <div className="ov-absent-grid">
            {alerts.absent_today.map((emp, i) => (
              <div key={i} className="ov-absent-card">
                <div className={`ov-absent-av ${getAvatarClass(emp.full_name)}`}>
                  {getInitials(emp.full_name)}
                </div>
                <div className="ov-absent-info">
                  <span className="ov-absent-name">{emp.full_name}</span>
                  <span className="ov-absent-meta">
                    {emp.employee_id} · {emp.department || '—'} · {emp.city}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}